import { Component, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter, map } from 'rxjs/operators';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SeoService, SeoRouteData, PRODUCTION_ORIGIN } from './shared/services/seo.service';
import { GtmService } from './shared/services/gtm.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastModule, ConfirmDialogModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'interpanely';

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private seoService: SeoService,
    private titleService: Title,
    private gtmService: GtmService,
  ) {}

  ngOnInit(): void {
    this.gtmService.load();

    this.seoService.setJsonLd('ld-organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Interpanely',
      url: PRODUCTION_ORIGIN,
      logo: `${PRODUCTION_ORIGIN}/images/interpanely_logo_light.png`,
      email: 'contact@arrayscript.com',
      sameAs: [
        'https://www.linkedin.com/company/arrayscript',
        'https://x.com/arrayscript',
        'https://www.facebook.com/arrayscript',
      ],
    });
    this.seoService.setJsonLd('ld-website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Interpanely',
      url: PRODUCTION_ORIGIN,
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route: ActivatedRouteSnapshot = this.activatedRoute.snapshot;
          // Merge `data` down the activated-route chain, leaf wins, so a
          // lazily-loaded child's `seo` can override (or simply define) what
          // its parent shell route left unset.
          let mergedSeo: SeoRouteData | undefined;
          let hasExplicitTitle = false;
          let node: ActivatedRouteSnapshot | null = route;
          while (node) {
            if (node.data?.['seo']) {
              mergedSeo = { ...mergedSeo, ...node.data['seo'] };
            }
            if (node.routeConfig?.title) {
              hasExplicitTitle = true;
            }
            node = node.firstChild;
          }
          return { mergedSeo, hasExplicitTitle };
        }),
      )
      .subscribe(({ mergedSeo, hasExplicitTitle }) => {
        // Angular's default title strategy only touches document.title when a
        // route defines an explicit `title`. Routes that don't define one
        // (e.g. a page added later without a title) would otherwise keep
        // showing whatever the previous page's title was, which is
        // confusing (looks like you're "still on" the old page). Force a
        // sane fallback any time a route hasn't set its own title.
        if (!hasExplicitTitle) {
          this.titleService.setTitle('Interpanely');
        }
        this.seoService.applyRouteSeo(mergedSeo, this.titleService.getTitle());
      });
  }
}
